import React from "react";
import MockupView from "./MockupView";
import SheetView from "./SheetView";
import KissView from "./KissView";
import { SHEET_GAP } from "../lib/nesting";

export default function StickerPreview({
  originalUrl,
  previewUrl,
  holoUrl,
  material,
  cutType,
  aspect = 1,
  viewMode = "surface",
  mockup = "laptop",
  widthIn = 3,
  heightIn = 3,
  sheetGap = SHEET_GAP,
}) {
  const isTransparent = material === "transparent_vinyl";
  const isHolo = material === "holographic_vinyl";
  const isChrome = material === "chrome_vinyl";

  // The applied sticker always uses the die-cut contour image (holo variant if holo).
  const stickerSrc = isHolo && holoUrl ? holoUrl : previewUrl;

  const wide = aspect >= 1;
  const boxStyle = wide
    ? { width: "min(64%, 400px)", aspectRatio: `${aspect}` }
    : { height: "min(64%, 400px)", aspectRatio: `${aspect}` };

  const maskStyle = (url) => ({
    WebkitMaskImage: `url(${url})`,
    maskImage: `url(${url})`,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  });

  if (viewMode === "context" && stickerSrc) {
    return (
      <MockupView stickerSrc={stickerSrc} isHolo={isHolo} isChrome={isChrome} widthIn={widthIn} heightIn={heightIn} mockup={mockup} />
    );
  }

  if (!stickerSrc) {
    return (
      <div className="relative w-full h-[460px] sm:h-[540px] rounded-2xl overflow-hidden flex items-center justify-center macbook-surface">
        <div className="text-center px-6 relative">
          <div className="text-6xl mb-3 animate-float text-nbblack/40">✦</div>
          <p className="font-head font-bold text-xl text-nbblack/50">Upload a PNG to preview your sticker</p>
        </div>
      </div>
    );
  }

  const StickerImg = ({ className = "", style = {} }) => (
    <div className={`relative ${className}`} style={style}>
      <img
        src={stickerSrc}
        alt="Sticker preview"
        data-testid="sticker-preview-img"
        className="w-full h-full object-contain drop-shadow-[1px_2px_2px_rgba(0,0,0,0.22)]"
        style={{ mixBlendMode: isTransparent ? "multiply" : "normal" }}
      />
      {isHolo && (
        <div className="holo-sheen absolute inset-0 pointer-events-none" style={{ ...maskStyle(stickerSrc), mixBlendMode: "screen", opacity: 0.6 }} />
      )}
      {isChrome && (
        <div className="chrome-sheen absolute inset-0 pointer-events-none" style={{ ...maskStyle(stickerSrc), mixBlendMode: "screen", opacity: 0.6 }} />
      )}
    </div>
  );

  // --- Sheet cut: nested on an 8.5x11 page, pixel-accurate true scale ---
  if (cutType === "sheet_cut") {
    return (
      <SheetView
        stickerSrc={stickerSrc}
        isTransparent={isTransparent}
        footW={widthIn}
        footH={heightIn}
        gap={sheetGap}
      />
    );
  }

  // --- Kiss cut: die-cut sticker on a backing liner sized 0.25" beyond the sticker edge ---
  if (cutType === "kiss_cut") {
    return (
      <KissView
        stickerSrc={stickerSrc}
        isTransparent={isTransparent}
        isHolo={isHolo}
        footW={widthIn}
        footH={heightIn}
      />
    );
  }

  // --- Die cut: sticker on the macbook surface with a soft halo ---
  return (
    <div data-testid="sticker-stage" className="relative w-full h-[460px] sm:h-[540px] rounded-2xl overflow-hidden flex items-center justify-center macbook-surface">
      <div className="relative flex items-center justify-center w-full h-full p-8">
        <div className="sticker-halo absolute pointer-events-none" style={{ width: "70%", height: "70%" }} />
        <StickerImg style={boxStyle} />
      </div>

      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur nb-border rounded-full px-3 py-1 text-xs font-head font-bold">
        {isHolo ? "Holographic foil" : isChrome ? "Chrome mirror" : isTransparent ? "See-through vinyl" : "White vinyl"}
      </div>
    </div>
  );
}
